// DR-13 UI-10/11/21: eine Form für Datum, Uhrzeit und Monat in der ganzen Oberfläche (de-AT, Ortszeit des Browsers):
// „18.10.2026, 00:59“, „18.10.2026“, „00:59“, „Oktober 2026“; ohne „Uhr“ und ohne Sekunden. Nur für angezeigten Text;
// Werte für Schnittstellen, Dateinamen, CSV und Sortierung bleiben, wie sie sind.

type Zeitwert = Date | string | number;

const DATUM_ZEIT = new Intl.DateTimeFormat('de-AT', { dateStyle: 'medium', timeStyle: 'short' });
const DATUM = new Intl.DateTimeFormat('de-AT', { dateStyle: 'medium' });
const UHRZEIT = new Intl.DateTimeFormat('de-AT', { timeStyle: 'short' });
const WOCHENTAG_ZEIT = new Intl.DateTimeFormat('de-AT', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
const MONAT = new Intl.DateTimeFormat('de-AT', { month: 'long', year: 'numeric' });
const MONAT_UTC = new Intl.DateTimeFormat('de-AT', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DATUM_UTC = new Intl.DateTimeFormat('de-AT', { dateStyle: 'medium', timeZone: 'UTC' });

const zeit = (wert: Zeitwert) => wert instanceof Date ? wert : new Date(wert);
const mit = (format: Intl.DateTimeFormat) => (wert: Zeitwert) => {
  const d = zeit(wert);
  return Number.isNaN(d.getTime()) ? '—' : format.format(d);
};

/** „18.10.2026, 00:59“ */
export const datumZeit = mit(DATUM_ZEIT);
/** „18.10.2026“ */
export const datum = mit(DATUM);
/** „00:59“ */
export const uhrzeit = mit(UHRZEIT);
/** „Mi., 21:32“ */
export const wochentagZeit = mit(WOCHENTAG_ZEIT);
/** „Oktober 2026“ für einen Zeitpunkt. */
export const monat = mit(MONAT);

/** Ein Kalendermonat „2026-10“ (Schlüssel wie am Server) als „Oktober 2026“. */
export const monatAusSchluessel = (schluessel: string) => MONAT_UTC.format(new Date(`${schluessel.slice(0, 7)}-01T00:00:00Z`));
/** Ein Kalendertag „2026-10-18“ (vom Server schon als Tag bestimmt) als „18.10.2026“, ohne Umrechnung in eine Zeitzone. */
export const datumAusTag = (tag: string) => DATUM_UTC.format(new Date(`${tag.slice(0, 10)}T00:00:00Z`));

export type Zeitgruppe = 'heute' | 'gestern' | 'woche' | 'aelter';

/** UI-11: die Gruppe einer Liste nach Kalendertagen der Ortszeit (Heute, Gestern, Letzte 7 Tage, Älter). */
export function zeitgruppe(wert: Zeitwert, jetzt: Date = new Date()): Zeitgruppe {
  const d = zeit(wert);
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  if (d >= heute) return 'heute';
  if (d >= new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - 1)) return 'gestern';
  if (d >= new Date(heute.getFullYear(), heute.getMonth(), heute.getDate() - 7)) return 'woche';
  return 'aelter';
}

/** UI-11: Heute und Gestern die Uhrzeit, Letzte 7 Tage Wochentag und Uhrzeit, Älter das Datum. */
export function listenZeit(wert: Zeitwert, jetzt: Date = new Date()): string {
  const gruppe = zeitgruppe(wert, jetzt);
  return gruppe === 'heute' || gruppe === 'gestern' ? uhrzeit(wert) : gruppe === 'woche' ? wochentagZeit(wert) : datum(wert);
}
